import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { ASSETS } from '../config/assets';
import { BarChart3, TrendingUp, Eye, Activity } from 'lucide-react';

const InsightsPage = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: TrendingUp,
      title: t('insights.feature1Title'),
      desc: t('insights.feature1Desc'),
    },
    {
      icon: Activity,
      title: t('insights.feature2Title'),
      desc: t('insights.feature2Desc'),
    },
    {
      icon: Eye,
      title: t('insights.feature3Title'),
      desc: t('insights.feature3Desc'),
    },
  ];

  return (
    <div data-testid="insights-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-[#1A1A1A] to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-8 h-8 text-wine" strokeWidth={1.5} />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine">
                {t('insights.title')}
              </span>
            </div>
            <h1 
              data-testid="insights-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
            >
              {t('insights.subtitle')}
            </h1>
            <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
              {t('insights.heroDesc')}
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                data-testid="insights-cta"
                className="btn-demo-cta text-white rounded-lg px-8 py-6"
              >
                {t('nav.requestDemo')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Dashboard Image */}
      <section className="py-12 lg:py-16 bg-[#0D0D0D]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <img
              src={ASSETS.DASHBOARD_ROI}
              alt="Insights dashboard"
              className="rounded-2xl shadow-2xl w-full object-cover border border-white/10"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0D0D0D]/40 to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`insights-feature-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-wine/30 transition-all duration-300"
              >
                <div className="p-3 bg-wine/10 rounded-xl w-fit mb-6">
                  <feature.icon className="w-6 h-6 text-wine" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
